import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import {
  TrendingUp,
  Warning,
  CheckCircle,
  Error,
  Assessment,
  Download
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR, enUS, es } from 'date-fns/locale';
import { format, subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import analyticsService from '../../services/analyticsService';
import StatusChart from '../../components/Charts/StatusChart';

const Analytics = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState({});
  const [equipmentMetrics, setEquipmentMetrics] = useState([]);
  const [complianceReport, setComplianceReport] = useState([]);
  const [expiredEquipment, setExpiredEquipment] = useState([]);
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [reportType, setReportType] = useState('all');

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'pt': return ptBR;
      case 'es': return es;
      default: return enUS;
    }
  };

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        type: reportType
      };

      const [metricsResponse, complianceResponse, expiredResponse] = await Promise.all([
        analyticsService.getMetrics(params),
        analyticsService.getComplianceReport(params),
        analyticsService.getExpiredEquipment({ limit: 10 })
      ]);

      if (metricsResponse.data.kpis) {
        setKpis(metricsResponse.data.kpis);
      }
      if (metricsResponse.data.equipment) {
        setEquipmentMetrics(metricsResponse.data.equipment);
      }
      
      setComplianceReport(complianceResponse.data || []);
      setExpiredEquipment(expiredResponse.data || []);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, reportType]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'valid': return 'success';
      case 'expiring': return 'warning';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid': return <CheckCircle />;
      case 'expiring': return <Warning />;
      case 'expired': return <Error />;
      default: return <Assessment />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateLocale()}>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h4" gutterBottom>
          📊 {t('analytics.title')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <DatePicker
                  label={t('analytics.startDate')}
                  value={startDate}
                  onChange={setStartDate}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <DatePicker
                  label={t('analytics.endDate')}
                  value={endDate}
                  onChange={setEndDate}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('analytics.reportType')}</InputLabel>
                  <Select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    label={t('analytics.reportType')}
                  >
                    <MenuItem value="all">{t('analytics.all')}</MenuItem>
                    <MenuItem value="kpis">{t('analytics.kpis')}</MenuItem>
                    <MenuItem value="equipment">{t('analytics.equipment')}</MenuItem>
                    <MenuItem value="alarms">{t('analytics.alarms')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  fullWidth
                  onClick={() => {
                    const style = document.createElement('style');
                    style.innerHTML = '@media print { .MuiDrawer-root, nav, .sidebar { display: none !important; } }';
                    document.head.appendChild(style);
                    window.print();
                    document.head.removeChild(style);
                  }}
                >
                  {t('analytics.export')}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {(reportType === 'all' || reportType === 'kpis' || reportType === 'alarms') && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TrendingUp color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      {t('analytics.totalEquipment')}
                    </Typography>
                    <Typography variant="h4">
                      {kpis.totalEquipment || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Error color="error" sx={{ mr: 1 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      {t('analytics.expiredEquipment')}
                    </Typography>
                    <Typography variant="h4" color="error">
                      {kpis.expiredEquipment || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Warning color="warning" sx={{ mr: 1 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      {t('analytics.activeAlarms')}
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {kpis.activeAlarms || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Assessment color="info" sx={{ mr: 1 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      {t('analytics.passagesToday')}
                    </Typography>
                    <Typography variant="h4" color="info.main">
                      {kpis.totalPassages || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        )}

        <Grid container spacing={3}>
          {(reportType === 'all' || reportType === 'equipment') && (
          <>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <StatusChart 
                  data={equipmentMetrics} 
                  title={t('analytics.statusDistribution')} 
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('analytics.equipmentStatus')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('analytics.category')}</TableCell>
                        <TableCell>{t('common.status')}</TableCell>
                        <TableCell align="right">{t('common.quantity')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {equipmentMetrics && equipmentMetrics.length > 0 ? (
                        equipmentMetrics.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Chip 
                                label={t(`equipment.${item.category}`, item.category)} 
                                size="small"
                                color={item.category === 'critical' ? 'error' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={getStatusIcon(item.status)}
                                label={t(`common.${item.status}`, item.status)}
                                size="small"
                                color={getStatusColor(item.status)}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="h6">
                                {item.count}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography color="textSecondary">
                              {t('analytics.noDataAvailable')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('analytics.topExpiredEquipment')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('analytics.equipment')}</TableCell>
                        <TableCell>{t('analytics.category')}</TableCell>
                        <TableCell align="right">{t('analytics.daysExpired')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expiredEquipment.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.equipment_name}</TableCell>
                          <TableCell>
                            <Chip 
                              label={t(`equipment.${item.category}`, item.category)} 
                              size="small"
                              color={item.category === 'critical' ? 'error' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography color="error" fontWeight="bold">
                              {item.days_expired}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('analytics.complianceReport')}
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('analytics.equipment')}</TableCell>
                        <TableCell>{t('analytics.category')}</TableCell>
                        <TableCell>{t('analytics.expirationDate')}</TableCell>
                        <TableCell>{t('common.status')}</TableCell>
                        <TableCell>{t('analytics.supplier')}</TableCell>
                        <TableCell align="right">{t('analytics.alarmCount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {complianceReport.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.equipment_name}</TableCell>
                          <TableCell>
                            <Chip 
                              label={t(`equipment.${item.category}`, item.category)} 
                              size="small"
                              color={item.category === 'critical' ? 'error' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            {item.expiration_date ? 
                              format(new Date(item.expiration_date), 'dd/MM/yyyy') : 
                              '-'
                            }
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(item.status)}
                              label={t(`common.${item.status}`, item.status)}
                              size="small"
                              color={getStatusColor(item.status)}
                            />
                          </TableCell>
                          <TableCell>{item.supplier_name || '-'}</TableCell>
                          <TableCell align="right">
                            {item.alarm_count > 0 && (
                              <Chip 
                                label={item.alarm_count} 
                                size="small" 
                                color="warning"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
          </>
          )}
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default Analytics;